import React, { useState, useEffect, useRef } from 'react'
import { useHospitalSelection } from '../../hooks/useHospitalSelection'
import { usePersistedState } from '../../hooks/usePersistedState'
import DateRangeSlider from '../../components/DateRangeSlider/DateRangeSlider'
import LoadingNotice from '../../components/LoadingNotice/LoadingNotice'
import { useClickOutside } from '../../hooks/useClickOutside'
import { TmbControlsRow, TmbStratificationPill, TmbQuestionPill, TmbPillSelect } from '../../components/TMBControls'
import {
    normalizeKey,
    safeNumber,
    findColumnIndex,
    findColumnIndexByRegex,
    parseMonthToValue,
    resolveMonthIdx,
    formatDateLabel,
    COLUMN_CANDIDATES
} from '../../utils/dataUtils'
import {
    MonthInfo,
    QuestionInfo,
    HuddleTotals,
    normalizeHuddleGroup,
    generateMonthList,
    extractQuestionsFromRows,
    buildQuestionLabelMap,
    initializeSliderValues,
    getSliderIndicesFromValues,
    TMB_SLIDER_KEY,
    extractActualMonthValues,
    checkHasDataForRange
} from '../../utils/tmbUtils'
import { generatePDFIds } from '../../utils/pdfIdHelper'
import { exportToPNG, exportToSVG, exportToCSV } from '../../utils/tmbExportUtils'
import { useTmbSlider } from '../../context/TmbSliderContext'
import { applyLaunchDateFilterToZohoData } from '../../utils/tmbLaunchDates'
import '../../styles/shared/index.css'
import './AgreementComplications.css'

const PAGE_HEADING_LINE_1 = 'Percent of Patients who "Strongly Agree"'
const PAGE_HEADING_LINE_2 = 'by Huddle Status & Complications'

const BASE_MAX_COMPLICATIONS_SELECTIONS = 6

const CANONICAL_COMPLICATIONS: { label: string; aliases: string[] }[] = [
    {
        label: "Complications",
        aliases: ["experiencedcomplication", "experiencedcomplications", "complication", "complications", "experienced", "yes", "true", "1", "anycomplication"]
    },
    {
        label: "No Complications",
        aliases: ["nocomplications", "nocomplication", "no", "none", "false", "0", "nil"]
    },
    {
        label: "Not reported",
        aliases: ["notreported"]
    }
]

const AgreementComplications: React.FC = () => {
    const { selectedHospitalIds, refreshKey } = useHospitalSelection()
    const { globalMonthList } = useTmbSlider()
    const [rawData, setRawData] = useState<any>(null)
    const [monthList, setMonthList] = useState<MonthInfo[]>([])
    const [actualMonths, setActualMonths] = useState<Set<number>>(new Set())
    const [loading, setLoading] = useState(false)
    const [initialLoading, setInitialLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const persistenceKey = `${TMB_SLIDER_KEY}_${Array.isArray(selectedHospitalIds) ? selectedHospitalIds.join('_') : selectedHospitalIds}`
    const [sliderRange, setSliderRange] = usePersistedState<[number, number]>(persistenceKey, [0, 0])
    const [sliderStartIdx, sliderEndIdx] = getSliderIndicesFromValues(globalMonthList, sliderRange)
    const hasDataForRange = checkHasDataForRange(sliderRange[0], sliderRange[1], actualMonths)

    const [selectedQuestion, setSelectedQuestion] = usePersistedState<string>('agreement_complications_question', '')
    const [stratification, setStratification] = usePersistedState<string>('agreement_complications_stratification', 'ExcludeNotSure')

    const [selectedComplicationTypes, setSelectedComplicationTypes] = usePersistedState<string[]>(
        'agreement_complications_types',
        ['Complications', 'No Complications', 'Not reported']
    )
    const [availableComplicationTypes, setAvailableComplicationTypes] = useState<string[]>([])

    const [complicationsDropdownOpen, setComplicationsDropdownOpen] = useState(false)
    const [questions, setQuestions] = useState<QuestionInfo[]>([])

    const chartContainerRef = useRef<HTMLDivElement>(null)
    const [menuOpen, setMenuOpen] = useState(false)
    const downloadMenuRef = useRef<HTMLDivElement>(null)

    const [chartCategories, setChartCategories] = useState<string[]>([])
    const [chartSeries, setChartSeries] = useState<{ name: string; data: number[] }[]>([])
    const [nLabels, setNLabels] = useState<{ yes: number[]; no: number[] }>({ yes: [], no: [] })
    const [hoverTooltip, setHoverTooltip] = useState<{ visible: boolean, x: number, y: number, content: React.ReactNode } | null>(null)

    const [columnIndices, setColumnIndices] = useState<{
        monthIdx: number
        questionNameIdx: number
        questionLabelIdx: number
        stratIdx: number
        huddleIdx: number
        numeratorIdx: number
        denominatorIdx: number
        complicationsIdx: number
    } | null>(null)
    const [isMobile, setIsMobile] = useState(false);
 
    const canonicalComplicationLabel = (v: string): string => {
        const k = normalizeKey(v)
        for (const entry of CANONICAL_COMPLICATIONS) {
            if (entry.aliases.includes(k)) return entry.label
        }
        return v
    }

    const handleDownloadPNG = async () => {
        if (!chartContainerRef.current) return
        const filename = `Agreement_Complications_${selectedQuestion || 'Question'}`
        const subtitle = questions.find(q => q.name === selectedQuestion)?.fullText || ''
        await exportToPNG(chartContainerRef.current, filename, 
            { title: `${PAGE_HEADING_LINE_1} ${PAGE_HEADING_LINE_2}`, subtitle },
            { cssContextClass: 'agreement-complications-page', hideSelectors: ['.chart-subtitle'], scale: 3 }
        )
    }

    const handleDownloadCSV = () => {
        const headers = ['page', 'hospital_ids', 'stratification', 'stratification_label', 'date_start', 'date_end', 'question', 'question_text', 'huddle_group', 'complication_type', 'numerator', 'denominator', 'percent']
        const rows: (string | number)[][] = []

        const dateStart = globalMonthList[sliderStartIdx]?.label || ''
        const dateEnd = globalMonthList[sliderEndIdx]?.label || ''

        const dateStartFormatted = formatDateLabel(dateStart)
        const dateEndFormatted = formatDateLabel(dateEnd)

        const stratLabel = stratification === 'ExcludeNotSure' ? 'Exclude "Not"' : 'Include "Not"'

        const questionCode = selectedQuestionData?.code || ''
        const questionText = selectedQuestionData?.fullText || ''

        const hospitalIds = Array.isArray(selectedHospitalIds) ? selectedHospitalIds.join(',') : selectedHospitalIds

        selectedComplicationTypes.forEach((compType, idx) => {
            const yesVal = chartSeries[0]?.data[idx] || 0
            const noVal = chartSeries[1]?.data[idx] || 0
            const yesDenom = nLabels.yes[idx] || 0
            const noDenom = nLabels.no[idx] || 0

            const yesNum = yesDenom > 0 ? Math.round((yesVal / 100) * yesDenom) : 0
            const noNum = noDenom > 0 ? Math.round((noVal / 100) * noDenom) : 0

            rows.push([
                'agreement_complications',
                hospitalIds,
                stratification,
                stratLabel,
                dateStartFormatted,
                dateEndFormatted,
                questionCode,
                questionText,
                'Yes Huddle',
                compType,
                yesNum,
                yesDenom,
                `${yesVal}%`
            ])

            rows.push([
                'agreement_complications',
                hospitalIds,
                stratification,
                stratLabel,
                dateStartFormatted,
                dateEndFormatted,
                questionCode,
                questionText,
                'No Huddle',
                compType,
                noNum,
                noDenom,
                `${noVal}%`
            ])
        })

        exportToCSV({ headers, rows, filename: `Agreement_Complications_${selectedQuestion || 'Question'}` })
    }

    const handleDownloadSVG = async () => {
        if (!chartContainerRef.current) return
        const filename = `Agreement_Complications_${selectedQuestion || 'Question'}`
        const subtitle = questions.find(q => q.name === selectedQuestion)?.fullText || ''
        await exportToSVG(chartContainerRef.current, filename, 
            { title: `${PAGE_HEADING_LINE_1} ${PAGE_HEADING_LINE_2}`, subtitle },
            { cssContextClass: 'agreement-complications-page', hideSelectors: ['.chart-subtitle'] }
        )
    }

    const handleComplicationsToggle = (compType: string, checked: boolean) => {
        if (checked) {
            if (selectedComplicationTypes.length >= BASE_MAX_COMPLICATIONS_SELECTIONS) {
                return
            }
            if (!selectedComplicationTypes.includes(compType)) {
                setSelectedComplicationTypes([...selectedComplicationTypes, compType])
            }
        } else {
            setSelectedComplicationTypes(selectedComplicationTypes.filter(t => t !== compType))
        }
    }

    useEffect(() => {
        if (!selectedHospitalIds || selectedHospitalIds.length === 0) {
            setInitialLoading(false)
            setRawData(null)
            setMonthList([])
            setQuestions([])
            setError(null)
            return
        }

        const hospitalIdsParam = Array.isArray(selectedHospitalIds)
            ? selectedHospitalIds.join(',')
            : selectedHospitalIds

        setLoading(true)
        setError(null)
        setInitialLoading(true)

        Promise.all([
            fetch(`/api/tmb/agreement_complications/${hospitalIdsParam}`).then(res => {
                if (!res.ok) {
                    if (res.status === 404) throw new Error('No data available for selected hospital(s)')
                    return res.json().then(errData => { throw new Error(errData.error || `API error: ${res.status}`) })
                }
                return res.json()
            }),
            fetch(`/api/tmb/agreement_single/${hospitalIdsParam}`).then(res => {
                if (!res.ok) return null
                return res.json()
            }).catch(() => null)
        ])
            .then(([compData, singleData]) => {
                applyLaunchDateFilterToZohoData(compData)
                const rows = compData?.response?.result?.rows || []
                const columns = compData?.response?.result?.column_order || []

                if (rows.length === 0 || !columns.length) {
                    setError('No data available for selected hospital(s)')
                    setLoading(false)
                    setInitialLoading(false)
                    return
                }

                setRawData(compData)

                const monthIdx = resolveMonthIdx(columns, rows)
                const questionNameIdx = findColumnIndex(columns, COLUMN_CANDIDATES.questionName) !== -1
                    ? findColumnIndex(columns, COLUMN_CANDIDATES.questionName)
                    : findColumnIndexByRegex(columns, [/questionname/])
                const questionLabelIdx = findColumnIndex(columns, COLUMN_CANDIDATES.questionLabel) !== -1
                    ? findColumnIndex(columns, COLUMN_CANDIDATES.questionLabel)
                    : findColumnIndexByRegex(columns, [/questionlabel/, /questiontext/, /question/, /itemtext/, /item/])
                const stratIdx = findColumnIndex(columns, COLUMN_CANDIDATES.stratification) !== -1
                    ? findColumnIndex(columns, COLUMN_CANDIDATES.stratification)
                    : findColumnIndexByRegex(columns, [/strat/])
                const huddleIdx = findColumnIndex(columns, COLUMN_CANDIDATES.huddle) !== -1
                    ? findColumnIndex(columns, COLUMN_CANDIDATES.huddle)
                    : findColumnIndexByRegex(columns, [/huddlegroup/, /huddle/])
                const numeratorIdx = findColumnIndex(columns, COLUMN_CANDIDATES.numerator) !== -1
                    ? findColumnIndex(columns, COLUMN_CANDIDATES.numerator)
                    : findColumnIndexByRegex(columns, [/numerator/, /num/])
                const denominatorIdx = findColumnIndex(columns, COLUMN_CANDIDATES.denominator) !== -1
                    ? findColumnIndex(columns, COLUMN_CANDIDATES.denominator)
                    : findColumnIndexByRegex(columns, [/denominator/, /denom/])
                const complicationsIdx = findColumnIndex(columns, COLUMN_CANDIDATES.complications) !== -1
                    ? findColumnIndex(columns, COLUMN_CANDIDATES.complications)
                    : findColumnIndexByRegex(columns, [/complication/i])

                setColumnIndices({
                    monthIdx,
                    questionNameIdx,
                    questionLabelIdx,
                    stratIdx,
                    huddleIdx,
                    numeratorIdx,
                    denominatorIdx,
                    complicationsIdx
                })

                setActualMonths(extractActualMonthValues(rows, monthIdx))
                const months = generateMonthList(rows, monthIdx)
                setMonthList(months)

                if (months.length > 0) {
                    setSliderRange(prev => initializeSliderValues(months, prev, 12))
                }

                let labelFallbackMap: Map<string, string> | undefined
                if (singleData) {
                    const sRows = singleData?.response?.result?.rows || []
                    const sCols = singleData?.response?.result?.column_order || []
                    const sQnIdx = findColumnIndex(sCols, COLUMN_CANDIDATES.questionName) !== -1
                        ? findColumnIndex(sCols, COLUMN_CANDIDATES.questionName)
                        : findColumnIndexByRegex(sCols, [/questionname/])
                    const sQlIdx = findColumnIndex(sCols, COLUMN_CANDIDATES.questionLabel) !== -1
                        ? findColumnIndex(sCols, COLUMN_CANDIDATES.questionLabel)
                        : findColumnIndexByRegex(sCols, [/questionlabel/, /questiontext/, /question/, /itemtext/, /item/])
                    
                    labelFallbackMap = buildQuestionLabelMap(sRows, sQnIdx, sQlIdx)
                }

                const questionList = extractQuestionsFromRows(rows, questionNameIdx, questionLabelIdx, labelFallbackMap)
                setQuestions(questionList)
                if (questionList.length > 0 && !selectedQuestion) {
                    setSelectedQuestion(questionList[0].name)
                }

                if (complicationsIdx !== -1) {
                    setAvailableComplicationTypes(CANONICAL_COMPLICATIONS.map(c => c.label))
                } else {
                      const compSet = new Set<string>()
                    rows.forEach((r: any[]) => {
                        const val = String(r[complicationsIdx] || "").trim()
                        if (val) {
                            compSet.add(canonicalComplicationLabel(val))
                        }
                    })
                    const canonicalOrder = CANONICAL_COMPLICATIONS.map(c => c.label)
                    const found = Array.from(compSet)
                    const ordered = canonicalOrder.filter(c => found.includes(c))
                    const extras = found.filter(c => !canonicalOrder.includes(c))
                    setAvailableComplicationTypes([...ordered, ...extras])
                }

                setLoading(false)
                setInitialLoading(false)
            })
            .catch(err => {
                setError(err.message || 'Failed to load data')
                setLoading(false)
                setInitialLoading(false)
            })
    }, [selectedHospitalIds, refreshKey])

    useEffect(() => {
        if (!rawData || !columnIndices) {
            setChartCategories([])
            setChartSeries([])
            setNLabels({ yes: [], no: [] })
            return
        }

        const rows = rawData?.response?.result?.rows || []
        if (!rows.length) return

        const { monthIdx, questionNameIdx, stratIdx, huddleIdx, numeratorIdx, denominatorIdx, complicationsIdx } = columnIndices

        let filteredRows = rows

        if (monthIdx !== -1 && monthList.length > 0) {
            const minMonthVal = sliderRange[0] || 0
            const maxMonthVal = sliderRange[1] || 0
            if (minMonthVal > 0 || maxMonthVal > 0) {
                filteredRows = filteredRows.filter((r: any[]) => {
                    const v = parseMonthToValue(r[monthIdx])
                    return v >= minMonthVal && v <= maxMonthVal
                })
            }
        }

        if (selectedQuestion && questionNameIdx !== -1) {
            filteredRows = filteredRows.filter((r: any[]) => {
                return String(r[questionNameIdx] || "").trim() === selectedQuestion
            })
        }

        if (stratIdx !== -1) {
            filteredRows = filteredRows.filter((r: any[]) => {
                return normalizeKey(r[stratIdx]) === normalizeKey(stratification)
            })
        }

        const categories: string[] = []
        const yesHuddleData: number[] = []
        const noHuddleData: number[] = []
        const yesDenoms: number[] = []
        const noDenoms: number[] = []

        const complicationTypeOrder = CANONICAL_COMPLICATIONS.map(c => c.label)

        const sortedComplicationTypes = [...selectedComplicationTypes].sort((a, b) => {
            const aIndex = complicationTypeOrder.indexOf(a)
            const bIndex = complicationTypeOrder.indexOf(b)
            if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex
            if (aIndex !== -1) return -1
            if (bIndex !== -1) return 1
            return a.localeCompare(b)
        })

        sortedComplicationTypes.forEach(compType => {
            const typeRows = filteredRows.filter((r: any[]) => {
                if (complicationsIdx === -1) return false
                const rawVal = String(r[complicationsIdx] || "").trim()
                return canonicalComplicationLabel(rawVal) === compType
            })

            const totals: HuddleTotals = {
                'Yes Huddle': { num: 0, denom: 0 },
                'No Huddle': { num: 0, denom: 0 }
            }

            typeRows.forEach((r: any[]) => {
                if (huddleIdx === -1) return
                const g = normalizeHuddleGroup(r[huddleIdx])
                if (!totals[g]) return

                const num = numeratorIdx >= 0 ? safeNumber(r[numeratorIdx]) : null
                const denom = denominatorIdx >= 0 ? safeNumber(r[denominatorIdx]) : null
                if (num !== null) totals[g].num += num
                if (denom !== null) totals[g].denom += denom
            })

            categories.push(compType)

            const yesPct = totals['Yes Huddle'].denom > 0
                ? (totals['Yes Huddle'].num / totals['Yes Huddle'].denom) * 100
                : 0
            const noPct = totals['No Huddle'].denom > 0
                ? (totals['No Huddle'].num / totals['No Huddle'].denom) * 100
                : 0

            yesHuddleData.push(Math.round(yesPct))
            noHuddleData.push(Math.round(noPct))
            yesDenoms.push(totals['Yes Huddle'].denom)
            noDenoms.push(totals['No Huddle'].denom)
        })

        setChartCategories(categories)
        setChartSeries([
            { name: 'Yes Huddle', data: yesHuddleData },
            { name: 'No Huddle', data: noHuddleData }
        ])
        setNLabels({ yes: yesDenoms, no: noDenoms })

    }, [rawData, columnIndices, monthList, sliderRange, selectedQuestion, stratification, selectedComplicationTypes])
    useEffect(() => {
        const checkScreenSize = () => {
        setIsMobile(window.innerWidth <= 768); 
        };

        checkScreenSize();

        window.addEventListener("resize", checkScreenSize);

        return () => {
        window.removeEventListener("resize", checkScreenSize);
        };
    }, []);
    useClickOutside([downloadMenuRef], () => setMenuOpen(false), menuOpen)

    const selectedQuestionData = questions.find(q => q.name === selectedQuestion)

    const isMaxComplicationsSelected = selectedComplicationTypes.length >= BASE_MAX_COMPLICATIONS_SELECTIONS

    if (initialLoading) {
        return <LoadingNotice title="Loading agreement data..." />
    }

    if (error) {
        return (
            <div className="agreement-complications-page tmb-page">
                <div className="content-wrapper">
                    <div className="header-wrapper">
                        <span className="page-title title--fixed">
                            <span className="title-line">{PAGE_HEADING_LINE_1}</span>
                            <span className="title-line">{PAGE_HEADING_LINE_2}</span>
                        </span>
                    </div>
                    <div className="section-card" style={{ minHeight: 'auto' }}>
                        {error.includes('No data available') ? (
                            <div style={{ padding: '14px 12px', fontSize: '13px', fontWeight: 600, color: '#000000' }}>
                                No data available
                            </div>
                        ) : (
                            <p>Error: {error}</p>
                        )}
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div id={generatePDFIds.dashboard()} className="agreement-complications-page tmb-page">
            <div className="content-wrapper">
                <div className="header-wrapper header--stratified">
                    <span className="page-title title--fixed">
                        <span className="title-line">{PAGE_HEADING_LINE_1}</span>
                        <span className="title-line">{PAGE_HEADING_LINE_2}</span>
                    </span>

                    <div className="controls-col controls--row">
                        <TmbControlsRow>
                            {globalMonthList.length > 0 && (
                                <DateRangeSlider
                                    monthList={globalMonthList}
                                    startIndex={sliderStartIdx}
                                    endIndex={sliderEndIdx}
                                    onChange={(start, end) => setSliderRange([globalMonthList[start]?.value || 0, globalMonthList[end]?.value || 0])}
                                />
                            )}

                            <TmbPillSelect
                                displayText={selectedComplicationTypes.length === 0 ? "Complications" : (selectedComplicationTypes.length === 1 ? selectedComplicationTypes[0] : `${selectedComplicationTypes.length} selected`)}
                                variant="multiselect"
                                isOpen={complicationsDropdownOpen}
                                onToggle={setComplicationsDropdownOpen}
                                style={{ minWidth: '100px' }}
                            >
                                <div className="complications-dropdown-content">
                                    {availableComplicationTypes.map(type => (
                                        <label
                                            key={type}
                                            className={`complications-item-label ${!selectedComplicationTypes.includes(type) && isMaxComplicationsSelected ? 'disabled' : ''}`}
                                        >
                                            <input
                                                type="checkbox"
                                                className="complications-checkbox"
                                                checked={selectedComplicationTypes.includes(type)}
                                                onChange={(e) => handleComplicationsToggle(type, e.target.checked)}
                                                disabled={!selectedComplicationTypes.includes(type) && isMaxComplicationsSelected}
                                            />
                                            {type}
                                        </label>
                                    ))}
                                </div>
                            </TmbPillSelect>

                            <TmbStratificationPill value={stratification} onChange={setStratification} />

                            <TmbQuestionPill questions={questions} selectedQuestion={selectedQuestion} onSelect={setSelectedQuestion} />

                        </TmbControlsRow>
                    </div>
                </div>

                <div className="section-card" ref={chartContainerRef} style={!hasDataForRange ? { minHeight: 'auto' } : undefined}>
                    {loading ? (
                        <div style={{ padding: '14px 12px', fontSize: '13px', fontWeight: 600, color: '#000000' }}>Loading…</div>
                    ) : !chartSeries.length || !chartCategories.length || !hasDataForRange ? (
                        <div style={{ padding: '14px 12px', fontSize: '13px', fontWeight: 600, color: '#000000' }}>No data available</div>
                    ) : (<>
                    <div className="custom-download-menu" ref={downloadMenuRef}>
                        <div className="hamburger-icon" onClick={() => setMenuOpen(!menuOpen)}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                                <g>
                                    <rect x="4" y="6" width="16" height="2" rx="1" />
                                    <rect x="4" y="11" width="16" height="2" rx="1" />
                                    <rect x="4" y="16" width="16" height="2" rx="1" />
                                </g>
                            </svg>
                        </div>
                        {menuOpen && (
                            <div className="download-dropdown show">
                                <div className="menu-items-row">
                                    <div className="menu-item" onClick={async () => { await handleDownloadPNG(); setMenuOpen(false); }}>Download PNG</div>
                                    <div className="menu-item" onClick={async () => { await handleDownloadSVG(); setMenuOpen(false); }}>Download SVG</div>
                                    <div className="menu-item" onClick={() => { handleDownloadCSV(); setMenuOpen(false); }}>Download CSV</div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="chart-subtitle" id="agreement-question-text">
                        {selectedQuestionData?.fullText || 'Select a question'}
                    </div>

                    <div className="complications-legend">
                        <div className="legend-item">
                            <span className="legend-dot no-huddle"></span>
                            <span>No Huddle</span>
                        </div>
                        <div className="legend-item">
                            <span className="legend-dot yes-huddle"></span>
                            <span>Yes Huddle</span>
                        </div>
                    </div>

                    <div className="chart-with-overlay">
                        {chartSeries[0]?.data?.length > 0 && (
                            <div className="complications-column-headers">
                                {chartCategories.map((cat, idx) => (
                                    <div key={idx} className="column-header-item">
                                        {cat}
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="complications-table-wrap" id="complications-table-wrap">
                                <table className="complications-bar-table">
                                    <colgroup>
                                        {chartCategories.map((_, i) => (
                                            <col key={i} style={{ width: `${100 / chartCategories.length}%` }} />
                                        ))}
                                    </colgroup>
                                    <tbody>
                                        <tr>
                                            {chartSeries[0]?.data.map((yesPct: number, i: number) => {
                                                const noPct = chartSeries[1]?.data[i] ?? 0
                                                const maxBarHeight = 320
                                                const yesHeight = Math.max(6, (yesPct / 100) * maxBarHeight)
                                                const noHeight = Math.max(6, (noPct / 100) * maxBarHeight)

                                                return (
                                                    <td key={i}>
                                                        <div className="bar-cell">
                                                            <div
                                                                className="bar-group"
                                                                onMouseEnter={(e) => {
                                                                    const rect = e.currentTarget.getBoundingClientRect();
                                                                    setHoverTooltip({
                                                                        visible: true,
                                                                        x: !isMobile? rect.right + 10: rect.right/2,
                                                                        y: rect.top + (rect.height / 2) - 30,
                                                                        content: (
                                                                            <div style={{ boxShadow: '2px 2px 6px -4px #999', cursor: 'default', zIndex: 9999, border: '1px solid #e3e3e3', backgroundColor: '#fff', minWidth: '150px', borderRadius: '5px', overflow: 'hidden' }}>
                                                                                <div className="apexcharts-tooltip-title" style={{ backgroundColor: '#ECEFF1', fontFamily: 'Helvetica, Arial, sans-serif', fontSize: '12px', borderBottom: '1px solid #ddd', padding: '8px 10px', margin: 0, color: '#000' }}>
                                                                                    No Huddle {nLabels.no[i] ?? 0}
                                                                                </div>
                                                                                <div style={{ padding: '8px 10px', backgroundColor: '#fff', display: 'flex', alignItems: 'center' }}>
                                                                                    <span style={{ backgroundColor: '#D79852', width: '10px', height: '10px', display: 'inline-block', borderRadius: '50%', marginRight: '8px' }}></span>
                                                                                    <span style={{ fontFamily: 'Helvetica, Arial, sans-serif', fontSize: '12px', color: '#000', marginRight: '5px' }}>Strongly Agree: </span>
                                                                                    <span style={{ fontFamily: 'Helvetica, Arial, sans-serif', fontSize: '12px', fontWeight: 600, color: '#000' }}>
                                                                                        {noPct}%
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                        )
                                                                    });
                                                                }}
                                                                onMouseLeave={() => setHoverTooltip(null)}
                                                            >
                                                                <div className="bar-value">{noPct}%</div>
                                                                <div
                                                                    className="bar-fill no-huddle"
                                                                    style={{ height: `${noHeight}px` }}
                                                                />
                                                            </div>
                                                            <div
                                                                className="bar-group"
                                                                onMouseEnter={(e) => {
                                                                    const rect = e.currentTarget.getBoundingClientRect();
                                                                    setHoverTooltip({
                                                                        visible: true,
                                                                        x: !isMobile? rect.right + 10: rect.right/2,
                                                                        y: rect.top + (rect.height / 2) - 30,
                                                                        content: (
                                                                            <div style={{ boxShadow: '2px 2px 6px -4px #999', cursor: 'default', zIndex: 9999, border: '1px solid #e3e3e3', backgroundColor: '#fff', minWidth: '150px', borderRadius: '5px', overflow: 'hidden' }}>
                                                                                <div className="apexcharts-tooltip-title" style={{ backgroundColor: '#ECEFF1', fontFamily: 'Helvetica, Arial, sans-serif', fontSize: '12px', borderBottom: '1px solid #ddd', padding: '8px 10px', margin: 0, color: '#000' }}>
                                                                                    Yes Huddle {nLabels.yes[i] ?? 0}
                                                                                </div>
                                                                                <div style={{ padding: '8px 10px', backgroundColor: '#fff', display: 'flex', alignItems: 'center' }}>
                                                                                    <span style={{ backgroundColor: '#3F548D', width: '10px', height: '10px', display: 'inline-block', borderRadius: '50%', marginRight: '8px' }}></span>
                                                                                    <span style={{ fontFamily: 'Helvetica, Arial, sans-serif', fontSize: '12px', color: '#000', marginRight: '5px' }}>Strongly Agree: </span>
                                                                                    <span style={{ fontFamily: 'Helvetica, Arial, sans-serif', fontSize: '12px', fontWeight: 600, color: '#000' }}>
                                                                                        {yesPct}%
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                        )
                                                                    });
                                                                }}
                                                                onMouseLeave={() => setHoverTooltip(null)}
                                                            >
                                                                <div className="bar-value">{yesPct}%</div>
                                                                <div
                                                                    className="bar-fill yes-huddle"
                                                                    style={{ height: `${yesHeight}px` }}
                                                                />
                                                            </div>
                                                        </div>
                                                    </td>
                                                )
                                            })}
                                        </tr>
                                    </tbody>
                                </table>
                        </div>

                        {chartSeries[0]?.data?.length > 0 && (
                            <div className="complications-n-values-row">
                                <span className="n-label">N=</span>
                                {chartCategories.map((_, idx) => (
                                    <div key={idx} className="n-value-column">
                                        <span className="n-value no">{nLabels.no[idx] ?? 0}</span>
                                        <span className="n-value yes">{nLabels.yes[idx] ?? 0}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    </>)}
                </div>
            </div>
            {hoverTooltip && hoverTooltip.visible && (
                <div style={{
                    position: 'fixed',
                    top: hoverTooltip.y,
                    left: hoverTooltip.x,
                    zIndex: 10000,
                    pointerEvents: 'none',
                    transform: 'translateY(-50%)'
                }}>
                    {hoverTooltip.content}
                </div>
            )}
        </div>
    )
}

export default AgreementComplications
